<template>
  <BaseDialog
    data-test-id="dialogConfirm"
    :max-width="500"
    v-bind="$attrs"
    v-model="model"
    :icon="icon"
    :icon-color="iconColor"
  >
    <slot>{{ message }}</slot>

    <slot name="confirmation-details"></slot>

    <template v-slot:actions>
      <ButtonSecondary
        @click="cancel"
        :loading="loading"
        :text="t('buttons.cancel')"
        v-if="mode === DialogConfirmMode.Delete || mode === DialogConfirmMode.Revert"
      />
      <v-spacer />
      <ButtonPrimary @click="confirm" :loading="loading" :text="primaryButtonText" />
    </template>
  </BaseDialog>
</template>

<script setup lang="ts">
// STORES, IMPORTS, & COMPOSABLES
import { DialogConfirmMode } from '@/enums/DialogConfirmMode'
import Colors from '@/styles/_colors.module.scss'
import { computed, type PropType } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n({ useScope: 'global' })

// PROPS & EMITS
const props = defineProps({
  mode: { type: String as PropType<DialogConfirmMode>, default: DialogConfirmMode.Delete },
  loading: { type: Boolean, default: false },
  message: { type: String, default: null }
})

const emit = defineEmits<{
  (e: 'delete'): void
  (e: 'revert'): void
  (e: 'cancel'): void
}>()

// REACTIVE VARIABLES
const model = defineModel<boolean>({ default: false })

// COMPUTED PROPERTIES
const icon = computed(() => {
  switch (props.mode) {
    case DialogConfirmMode.Delete: {
      return 'mdi-delete-circle'
    }
    case DialogConfirmMode.Revert: {
      return 'mdi-file-undo'
    }
    case DialogConfirmMode.Warn: {
      return 'mdi-alert'
    }
    default: {
      return 'mdi-information-slab-circle'
    }
  }
})

const iconColor = computed(() => {
  switch (props.mode) {
    case DialogConfirmMode.Delete: {
      return Colors.accentError
    }
    case DialogConfirmMode.Revert: {
      return Colors.accentPrimary
    }
    case DialogConfirmMode.Warn: {
      return Colors.accentPrimary
    }
    default: {
      return Colors.mainPrimary
    }
  }
})

const primaryButtonText = computed(() => {
  switch (props.mode) {
    case DialogConfirmMode.Delete: {
      return t('buttons.delete')
    }
    case DialogConfirmMode.Revert: {
      return t('buttons.revert')
    }
    default: {
      return t('buttons.ok')
    }
  }
})

const cancel = () => {
  emit('cancel')
}

const confirm = () => {
  switch (props.mode) {
    case DialogConfirmMode.Delete: {
      emit('delete')
      break
    }
    case DialogConfirmMode.Revert: {
      emit('revert')
      break
    }
    default: {
      emit('cancel')
      break
    }
  }
}
</script>
